import {
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import * as naverCommerceMonitoringApi from '@apis/naver-commerce-monitoring';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { FilterSelect } from '@components/common/FilterSelect';
import dayjs from 'dayjs';
import React from 'react';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const NaverCommerceMonitoringPage: React.FC = () => {
  const [apiCallsLimit, setApiCallsLimit] = React.useState(50);
  const [apiCallsOffset, setApiCallsOffset] = React.useState(0);
  const [apiCallsCurrentPage, setApiCallsCurrentPage] = React.useState(1);
  const [selectedStatusCode, setSelectedStatusCode] = React.useState<number | null>(null);
  const [searchUrl, setSearchUrl] = React.useState('');
  const [showFailedOnly, setShowFailedOnly] = React.useState(false);

  // 큐 모니터링 상태
  const [queueLimit, setQueueLimit] = React.useState(50);
  const [selectedPriority, setSelectedPriority] = React.useState<'high' | 'normal' | 'low' | 'all'>('all');
  const [selectedMethod, setSelectedMethod] = React.useState<string>('all');

  // 대시보드 데이터 조회
  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ['naver-commerce-dashboard'],
    queryFn: async () => {
      const response = await naverCommerceMonitoringApi.getNaverCommerceDashboard();
      return response.data;
    },
    refetchInterval: 10000, // 10초마다 자동 새로고침
  });

  // Rate Limit 리셋 뮤테이션
  const resetRateLimitMutation = useMutation({
    mutationFn: naverCommerceMonitoringApi.resetNaverCommerceRateLimit,
    onSuccess: () => {
      message.success('Rate Limit이 리셋되었습니다.');
      refetchDashboard();
    },
    onError: (error: any) => {
      message.error(`Rate Limit 리셋 실패: ${error.message}`);
    },
  });

  // // 강제 정리 뮤테이션
  // const forceCleanupMutation = useMutation({
  //   mutationFn: naverCommerceMonitoringApi.forceCleanupNaverCommerce,
  //   onSuccess: (data) => {
  //     message.success(`강제 정리 완료: ${data.data.cleanedQueueItems}개 아이템 정리됨`);
  //     refetchDashboard();
  //   },
  //   onError: (error: any) => {
  //     message.error(`강제 정리 실패: ${error.message}`);
  //   },
  // });

  // API 호출 내역 조회
  const {
    data: apiCallsData,
    isLoading: isLoadingApiCalls,
    refetch: refetchApiCalls,
  } = useQuery({
    queryKey: [
      'naver-commerce-api-calls',
      apiCallsLimit,
      apiCallsOffset,
      selectedStatusCode,
      searchUrl,
      showFailedOnly,
    ],
    queryFn: async () => {
      const response = await naverCommerceMonitoringApi.getNaverCommerceApiCalls(
        apiCallsLimit,
        apiCallsOffset,
        selectedStatusCode || undefined,
        searchUrl.trim() || undefined,
        showFailedOnly || undefined,
      );
      return response.data;
    },
    refetchInterval: 30000, // 30초마다 자동 새로고침
  });

  // 페이징 핸들러
  const handlePageChange = (page: number, pageSize?: number) => {
    const newPageSize = pageSize || apiCallsLimit;
    const newOffset = (page - 1) * newPageSize;

    setApiCallsCurrentPage(page);
    setApiCallsLimit(newPageSize);
    setApiCallsOffset(newOffset);
  };

  // 페이지 크기 변경 핸들러
  const handlePageSizeChange = (current: number, size: number) => {
    const newOffset = (current - 1) * size;
    setApiCallsCurrentPage(current);
    setApiCallsLimit(size);
    setApiCallsOffset(newOffset);
  };

  // 필터 초기화 함수
  const handleResetFilters = () => {
    setSelectedStatusCode(null);
    setSearchUrl('');
    setShowFailedOnly(false);
    setApiCallsCurrentPage(1);
    setApiCallsOffset(0);
  };

  // 서버에서 필터링된 데이터를 직접 사용
  const getApiCalls = () => {
    return apiCallsData?.calls || [];
  };

  const getTotalCount = () => {
    return apiCallsData?.total || 0;
  };

  // API 호출 통계 조회
  const {
    data: apiCallsStatsData,
    isLoading: isLoadingApiCallsStats,
    refetch: refetchApiCallsStats,
  } = useQuery({
    queryKey: ['naver-commerce-api-calls-stats'],
    queryFn: async () => {
      const response = await naverCommerceMonitoringApi.getNaverCommerceApiCallsStats();
      return response.data;
    },
    refetchInterval: 30000, // 30초마다 자동 새로고침
  });

  // 큐 상세 정보 조회
  const {
    data: queueDetailedData,
    isLoading: isLoadingQueueDetailed,
    refetch: refetchQueueDetailed,
  } = useQuery({
    queryKey: ['naver-commerce-queue-detailed'],
    queryFn: async () => {
      const response = await naverCommerceMonitoringApi.getQueueItemsDetailed();
      return response.data;
    },
    refetchInterval: 10000, // 10초마다 자동 새로고침
  });

  // 처리 중인 API 조회
  const {
    data: processingApiData,
    isLoading: isLoadingProcessingApi,
    refetch: refetchProcessingApi,
  } = useQuery({
    queryKey: ['naver-commerce-processing-apis'],
    queryFn: async () => {
      const response = await naverCommerceMonitoringApi.getProcessingApiItems();
      return response.data;
    },
    refetchInterval: 10000, // 10초마다 자동 새로고침
  });

  // 대기 중인 API 조회 (우선순위별)
  const {
    data: pendingApiData,
    isLoading: isLoadingPendingApi,
    refetch: refetchPendingApi,
  } = useQuery({
    queryKey: ['naver-commerce-pending-apis', selectedPriority, queueLimit],
    queryFn: async () => {
      if (selectedPriority === 'all') {
        const response = await naverCommerceMonitoringApi.getPendingApiItems(queueLimit);
        return response.data;
      } else {
        const response = await naverCommerceMonitoringApi.getPendingApiByPriority(selectedPriority, queueLimit);
        return response.data;
      }
    },
    refetchInterval: 10000, // 10초마다 자동 새로고침
  });

  // 스케줄된 API 조회
  const {
    data: scheduledApiData,
    isLoading: isLoadingScheduledApi,
    refetch: refetchScheduledApi,
  } = useQuery({
    queryKey: ['naver-commerce-scheduled-apis', queueLimit],
    queryFn: async () => {
      const response = await naverCommerceMonitoringApi.getScheduledApiItems(queueLimit);
      return response.data;
    },
    refetchInterval: 10000, // 10초마다 자동 새로고침
  });

  // 큐 모니터링 헬퍼 함수들
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'normal':
        return 'blue';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'processing':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockCircleOutlined />;
      case 'processing':
        return <LoadingOutlined />;
      case 'completed':
        return <CheckCircleOutlined />;
      case 'failed':
        return <CloseCircleOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const getMethodDisplayName = (method: string) => {
    switch (method) {
      case 'getNaverCommerceOrdersFrom':
        return '주문 조회';
      case 'getNaverCommerceApiToken':
        return '토큰 조회';
      case 'deliveryNaveOrder':
        return '배송 처리';
      case 'cancelApproveNaveOrder':
        return '주문 취소';
      default:
        return method;
    }
  };

  // 헬스 상태에 따른 색상 반환
  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  // 헬스 상태에 따른 아이콘 반환
  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'critical':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  // Rate Limit 리셋 확인 모달
  const handleResetRateLimit = () => {
    Modal.confirm({
      title: 'Rate Limit 리셋',
      content: 'Rate Limit을 리셋하시겠습니까?',
      okText: '리셋',
      cancelText: '취소',
      onOk: () => resetRateLimitMutation.mutate(),
    });
  };

  // // 강제 정리 확인 모달
  // const handleForceCleanup = () => {
  //   Modal.confirm({
  //     title: '강제 정리',
  //     content: '큐와 Rate Limit을 강제로 정리하시겠습니까? 이 작업은 긴급 상황에서만 사용하세요.',
  //     okText: '정리',
  //     cancelText: '취소',
  //     okType: 'danger',
  //     onOk: () => forceCleanupMutation.mutate(),
  //   });
  // };

  // 알림 테이블 컬럼
  const alertColumns = [
    {
      title: '타입',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color={type === 'critical' ? 'red' : 'orange'}>{type.toUpperCase()}</Tag>,
    },
    {
      title: '메시지',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: '시간',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: string) => dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  // API 호출 내역 테이블 컬럼
  const apiCallsColumns = [
    {
      title: '시간',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: string) => dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
      width: 150,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: (url: string) => (
        <Text style={{ fontSize: '12px' }} ellipsis={{ tooltip: url }}>
          {url}
        </Text>
      ),
      width: 300,
    },
    {
      title: '메서드',
      dataIndex: 'method',
      key: 'method',
      render: (method: string) => <Tag color="blue">{method}</Tag>,
      width: 80,
    },
    {
      title: '상태 코드',
      dataIndex: 'statusCode',
      key: 'statusCode',
      render: (statusCode: number) => (
        <Tag color={statusCode === 200 ? 'green' : statusCode >= 400 ? 'red' : 'orange'}>{statusCode}</Tag>
      ),
      width: 100,
    },
    {
      title: '응답 시간',
      dataIndex: 'responseTime',
      key: 'responseTime',
      render: (responseTime: number) => (responseTime ? `${responseTime}ms` : '-'),
      width: 100,
    },
    {
      title: '오류 메시지',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (errorMessage: string) =>
        errorMessage ? (
          <Text style={{ fontSize: '12px', color: '#ff4d4f' }} ellipsis={{ tooltip: errorMessage }}>
            {errorMessage}
          </Text>
        ) : (
          '-'
        ),
    },
  ];

  if (isLoadingDashboard) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>모니터링 데이터를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>네이버 커머스 API 모니터링</Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              refetchDashboard();
              refetchApiCalls();
              refetchApiCallsStats();
              refetchQueueDetailed();
              refetchProcessingApi();
              refetchPendingApi();
              refetchScheduledApi();
            }}
            loading={
              isLoadingDashboard ||
              isLoadingApiCalls ||
              isLoadingApiCallsStats ||
              isLoadingQueueDetailed ||
              isLoadingProcessingApi ||
              isLoadingPendingApi ||
              isLoadingScheduledApi
            }>
            새로고침
          </Button>
        </Space>
      </div>

      {/* 전체 상태 알림 */}
      {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
        <Alert
          message="시스템 알림"
          description={
            <div>
              {dashboardData.alerts.map((alert, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <Tag color={alert.type === 'critical' ? 'red' : 'orange'} style={{ marginRight: '8px' }}>
                    {alert.type.toUpperCase()}
                  </Tag>
                  {alert.message}
                </div>
              ))}
            </div>
          }
          type={dashboardData.alerts.some((alert) => alert.type === 'critical') ? 'error' : 'warning'}
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <Tabs defaultActiveKey="overview">
        <TabPane
          tab={
            <>
              <DashboardOutlined /> 개요
            </>
          }
          key="overview">
          {/* 개요 통계 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="큐 크기"
                  value={dashboardData?.overview.queueSize || 0}
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: (dashboardData?.overview.queueSize || 0) > 500 ? '#ff4d4f' : '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="처리 중"
                  value={dashboardData?.overview.processingCount || 0}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: (dashboardData?.overview.processingCount || 0) > 100 ? '#ff4d4f' : '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="사용 가능 토큰"
                  value={dashboardData?.overview.availableTokens || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: (dashboardData?.overview.availableTokens || 0) <= 2 ? '#ff4d4f' : '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="버스트 토큰"
                  value={dashboardData?.overview.burstTokens || 0}
                  prefix={<ThunderboltOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* 오늘 통계 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="오늘 총 요청"
                  value={dashboardData?.stats?.today?.total?.queued || 0}
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="완료된 요청"
                  value={dashboardData?.stats?.today?.total?.completed || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="실패한 요청"
                  value={dashboardData?.stats?.today?.total?.failed || 0}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="성공률"
                  value={dashboardData?.stats?.summary?.successRate || 0}
                  suffix="%"
                  precision={2}
                  prefix={<BarChartOutlined />}
                  valueStyle={{
                    color: (dashboardData?.stats?.summary?.successRate || 0) >= 90 ? '#3f8600' : '#ff4d4f',
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* 처리 상태 통계 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="대기 중인 요청"
                  value={
                    (dashboardData?.stats?.summary?.totalRequests || 0) -
                    (dashboardData?.stats?.summary?.completed || 0)
                  }
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{
                    color:
                      (dashboardData?.stats?.summary?.totalRequests || 0) -
                        (dashboardData?.stats?.summary?.completed || 0) >
                      1000
                        ? '#ff4d4f'
                        : '#faad14',
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="완료율"
                  value={Math.round(
                    ((dashboardData?.stats?.summary?.completed || 0) /
                      (dashboardData?.stats?.summary?.totalRequests || 1)) *
                      100,
                  )}
                  suffix="%"
                  precision={2}
                  prefix={<BarChartOutlined />}
                  valueStyle={{
                    color:
                      Math.round(
                        ((dashboardData?.stats?.summary?.completed || 0) /
                          (dashboardData?.stats?.summary?.totalRequests || 1)) *
                          100,
                      ) >= 80
                        ? '#3f8600'
                        : '#faad14',
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="재시도율"
                  value={dashboardData?.stats?.summary?.retryRate || 0}
                  suffix="%"
                  precision={2}
                  prefix={<ReloadOutlined />}
                  valueStyle={{ color: (dashboardData?.stats?.summary?.retryRate || 0) > 10 ? '#ff4d4f' : '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="재시도 횟수"
                  value={dashboardData?.stats?.today?.total?.retried || 0}
                  prefix={<ReloadOutlined />}
                  valueStyle={{
                    color: (dashboardData?.stats?.today?.total?.retried || 0) > 50 ? '#ff4d4f' : '#3f8600',
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* 메서드별 통계 */}
          <Card title="메서드별 오늘 통계" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              {dashboardData?.stats?.today?.byMethod &&
                Object.entries(dashboardData.stats.today.byMethod).map(([method, stats]: [string, any]) => (
                  <Col xs={24} sm={12} md={6} key={method}>
                    <Card size="small" style={{ height: '100%' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong style={{ fontSize: '14px' }}>
                          {method === 'getNaverCommerceOrdersFrom' && '주문 조회'}
                          {method === 'getNaverCommerceApiToken' && '토큰 조회'}
                          {method === 'deliveryNaveOrder' && '배송 처리'}
                          {method === 'cancelApproveNaveOrder' && '주문 취소'}
                          {![
                            'getNaverCommerceOrdersFrom',
                            'getNaverCommerceApiToken',
                            'deliveryNaveOrder',
                            'cancelApproveNaveOrder',
                          ].includes(method) && method}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>대기:</Text>
                        <Text style={{ fontSize: '12px' }}>{stats.queued || 0}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>완료:</Text>
                        <Text style={{ fontSize: '12px', color: '#3f8600' }}>{stats.completed || 0}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>실패:</Text>
                        <Text style={{ fontSize: '12px', color: '#ff4d4f' }}>{stats.failed || 0}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>재시도:</Text>
                        <Text style={{ fontSize: '12px', color: '#faad14' }}>{stats.retried || 0}</Text>
                      </div>
                    </Card>
                  </Col>
                ))}
            </Row>
          </Card>

          {/* 헬스 상태 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} md={12}>
              <Card title="시스템 헬스 상태">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {getHealthIcon(dashboardData?.overview.health.overall || 'unknown')}
                    <Text strong>전체 상태: </Text>
                    <Tag color={getHealthColor(dashboardData?.overview.health.overall || 'unknown')}>
                      {dashboardData?.overview.health.overall?.toUpperCase() || 'UNKNOWN'}
                    </Tag>
                  </div>
                  {dashboardData?.overview.health.issues && dashboardData.overview.health.issues.length > 0 && (
                    <div>
                      <Text strong>이슈:</Text>
                      <ul style={{ marginTop: '8px' }}>
                        {dashboardData.overview.health.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DatabaseOutlined />
                    <Text>Redis 상태: </Text>
                    <Tag color={dashboardData?.overview.health.redis ? 'success' : 'error'}>
                      {dashboardData?.overview.health.redis ? '정상' : '오류'}
                    </Tag>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Rate Limit 상태">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>현재 토큰: </Text>
                    <Text>{dashboardData?.rateLimit.tokens || 0}</Text>
                  </div>
                  <div>
                    <Text strong>버스트 토큰: </Text>
                    <Text>{dashboardData?.rateLimit.burstTokens || 0}</Text>
                  </div>
                  <div>
                    <Text strong>설정 정보: </Text>
                    <Text>Rate Limit 설정은 서버에서 관리됩니다.</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* 액션 버튼 */}
          <Card title="관리 액션">
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleResetRateLimit}
                loading={resetRateLimitMutation.isPending}>
                Rate Limit 리셋
              </Button>
              {/* <Button
                danger
                icon={<ExclamationCircleOutlined />}
                onClick={handleForceCleanup}
                loading={forceCleanupMutation.isPending}>
                강제 정리
              </Button> */}
            </Space>
          </Card>

          {/* 최근 오류 */}
          <Card title="최근 오류" style={{ marginTop: '24px' }}>
            <Table
              columns={[
                {
                  title: '시간',
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (timestamp: string) => dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
                  width: 150,
                },
                {
                  title: '메서드',
                  dataIndex: 'method',
                  key: 'method',
                  render: (method: string) => <Tag color="blue">{method}</Tag>,
                  width: 200,
                },
                {
                  title: '오류 메시지',
                  dataIndex: ['error', 'message'],
                  key: 'errorMessage',
                  render: (message: string) => (
                    <Text style={{ fontSize: '12px', color: '#ff4d4f' }} ellipsis={{ tooltip: message }}>
                      {message}
                    </Text>
                  ),
                },
                {
                  title: '상태 코드',
                  dataIndex: ['error', 'status'],
                  key: 'status',
                  render: (status: number) => <Tag color={status >= 400 ? 'red' : 'orange'}>{status}</Tag>,
                  width: 100,
                },
              ]}
              dataSource={dashboardData?.recentErrors || []}
              rowKey="timestamp"
              pagination={{ pageSize: 5 }}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>

          {/* 알림 */}
          <Card title="알림" style={{ marginTop: '24px' }}>
            <Table
              columns={alertColumns}
              dataSource={dashboardData?.alerts || []}
              rowKey="timestamp"
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>
        <TabPane
          tab={
            <>
              <BarChartOutlined /> 통계
            </>
          }
          key="stats">
          {/* API 호출 통계 */}
          <Card title="API 호출 통계" style={{ marginBottom: '24px' }}>
            {isLoadingApiCallsStats ? (
              <Spin />
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="총 호출 수"
                    value={apiCallsStatsData?.totalCalls || 0}
                    prefix={<DatabaseOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="성공 호출"
                    value={apiCallsStatsData?.successCalls || 0}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="실패 호출"
                    value={apiCallsStatsData?.errorCalls || 0}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="성공률"
                    value={apiCallsStatsData?.successRate || 0}
                    suffix="%"
                    precision={2}
                    prefix={<BarChartOutlined />}
                    valueStyle={{ color: (apiCallsStatsData?.successRate || 0) >= 90 ? '#3f8600' : '#ff4d4f' }}
                  />
                </Col>
              </Row>
            )}
          </Card>

          {/* 필터 및 액션 */}
          <Card title="필터 및 액션" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <Text strong>상태 코드:</Text>
                <FilterSelect
                  style={{ width: '100%', marginTop: '4px' }}
                  placeholder="상태 코드 선택"
                  allowClear
                  value={selectedStatusCode}
                  onChange={(value) => {
                    setSelectedStatusCode(value);
                    setApiCallsCurrentPage(1);
                    setApiCallsOffset(0);
                  }}
                  options={[
                    { label: '200 (성공)', value: 200 },
                    { label: '400 (잘못된 요청)', value: 400 },
                    { label: '401 (인증 실패)', value: 401 },
                    { label: '403 (권한 없음)', value: 403 },
                    { label: '404 (찾을 수 없음)', value: 404 },
                    { label: '429 (Rate Limit)', value: 429 },
                    { label: '500 (서버 오류)', value: 500 },
                  ]}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>URL 검색:</Text>
                <Input
                  style={{ marginTop: '4px' }}
                  placeholder="URL 패턴 입력"
                  value={searchUrl}
                  onChange={(e) => {
                    setSearchUrl(e.target.value);
                    setApiCallsCurrentPage(1);
                    setApiCallsOffset(0);
                  }}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>표시 개수:</Text>
                <Select
                  style={{ width: '100%', marginTop: '4px' }}
                  value={apiCallsLimit}
                  onChange={(value) => {
                    setApiCallsLimit(value);
                    setApiCallsCurrentPage(1);
                    setApiCallsOffset(0);
                  }}
                  options={[
                    { label: '25개', value: 25 },
                    { label: '50개', value: 50 },
                    { label: '100개', value: 100 },
                    { label: '200개', value: 200 },
                  ]}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>필터 옵션:</Text>
                <div style={{ marginTop: '4px' }}>
                  <Checkbox
                    checked={showFailedOnly}
                    onChange={(e) => {
                      setShowFailedOnly(e.target.checked);
                      setApiCallsCurrentPage(1);
                      setApiCallsOffset(0);
                    }}>
                    실패한 호출만 보기
                  </Checkbox>
                </div>
              </Col>
              <Col xs={24} sm={24} md={6}>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      refetchApiCalls();
                      refetchApiCallsStats();
                    }}
                    loading={isLoadingApiCalls || isLoadingApiCallsStats}>
                    새로고침
                  </Button>
                  <Button
                    onClick={handleResetFilters}
                    disabled={!selectedStatusCode && !searchUrl.trim() && !showFailedOnly}>
                    필터 초기화
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* API 호출 내역 테이블 */}
          <Card
            title={
              <div>
                API 호출 내역
                {(selectedStatusCode || searchUrl.trim() || showFailedOnly) && (
                  <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '4px', color: '#666' }}>
                    필터 적용:
                    {selectedStatusCode && <span style={{ marginLeft: '8px' }}>상태코드 {selectedStatusCode}</span>}
                    {searchUrl.trim() && <span style={{ marginLeft: '8px' }}>URL "{searchUrl}"</span>}
                    {showFailedOnly && <span style={{ marginLeft: '8px' }}>실패한 호출만</span>}
                    <span style={{ marginLeft: '8px', color: '#1890ff' }}>(총 {getTotalCount()}개)</span>
                  </div>
                )}
              </div>
            }>
            {isLoadingApiCalls ? (
              <Spin />
            ) : (
              <Table
                columns={apiCallsColumns}
                dataSource={getApiCalls()}
                rowKey="id"
                pagination={{
                  current: apiCallsCurrentPage,
                  pageSize: apiCallsLimit,
                  total: getTotalCount(),
                  onChange: handlePageChange,
                  onShowSizeChange: handlePageSizeChange,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`,
                  pageSizeOptions: ['25', '50', '100', '200'],
                  size: 'default',
                }}
                size="small"
                scroll={{ x: 800 }}
              />
            )}
          </Card>
        </TabPane>
        <TabPane
          tab={
            <>
              <DatabaseOutlined /> 큐 모니터링
            </>
          }
          key="queue">
          {/* 큐 개요 통계 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="전체 대기 중"
                  value={queueDetailedData?.summary?.totalPending || 0}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{
                    color: (queueDetailedData?.summary?.totalPending || 0) > 1000 ? '#ff4d4f' : '#1890ff',
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="처리 중"
                  value={queueDetailedData?.summary?.totalProcessing || 0}
                  prefix={<LoadingOutlined />}
                  valueStyle={{
                    color: (queueDetailedData?.summary?.totalProcessing || 0) > 100 ? '#ff4d4f' : '#3f8600',
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="스케줄된 요청"
                  value={queueDetailedData?.summary?.scheduledCount || 0}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{
                    color: (queueDetailedData?.summary?.scheduledCount || 0) > 50 ? '#ff4d4f' : '#faad14',
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="고우선순위 대기"
                  value={queueDetailedData?.summary?.pendingByPriority?.high || 0}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{
                    color: (queueDetailedData?.summary?.pendingByPriority?.high || 0) > 10 ? '#ff4d4f' : '#3f8600',
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* 우선순위별 통계 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={8}>
              <Card title="우선순위별 대기 요청">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>고우선순위:</Text>
                    <Tag color="red">{queueDetailedData?.summary?.pendingByPriority?.high || 0}개</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>일반:</Text>
                    <Tag color="blue">{queueDetailedData?.summary?.pendingByPriority?.normal || 0}개</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>낮은 우선순위:</Text>
                    <Tag color="green">{queueDetailedData?.summary?.pendingByPriority?.low || 0}개</Tag>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={16}>
              <Card title="메서드별 대기 요청">
                <Row gutter={[8, 8]}>
                  {queueDetailedData?.summary?.pendingByMethod &&
                    Object.entries(queueDetailedData.summary.pendingByMethod).map(([method, count]: [string, any]) => (
                      <Col xs={12} sm={8} md={6} key={method}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <Text style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                            {getMethodDisplayName(method)}
                          </Text>
                          <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                            {count}개
                          </Text>
                        </Card>
                      </Col>
                    ))}
                </Row>
              </Card>
            </Col>
          </Row>

          {/* 필터 및 액션 */}
          <Card title="필터 및 액션" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <Text strong>우선순위:</Text>
                <FilterSelect
                  inactiveValue="all"
                  style={{ width: '100%', marginTop: '4px' }}
                  value={selectedPriority}
                  onChange={(value) => setSelectedPriority(value)}
                  options={[
                    { label: '전체', value: 'all' },
                    { label: '고우선순위', value: 'high' },
                    { label: '일반', value: 'normal' },
                    { label: '낮은 우선순위', value: 'low' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>표시 개수:</Text>
                <Select
                  style={{ width: '100%', marginTop: '4px' }}
                  value={queueLimit}
                  onChange={(value) => setQueueLimit(value)}
                  options={[
                    { label: '25개', value: 25 },
                    { label: '50개', value: 50 },
                    { label: '100개', value: 100 },
                    { label: '200개', value: 200 },
                  ]}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Text strong>메서드:</Text>
                <Select
                  style={{ width: '100%', marginTop: '4px' }}
                  value={selectedMethod}
                  onChange={(value) => setSelectedMethod(value)}
                  options={[
                    { label: '전체', value: 'all' },
                    { label: '주문 조회', value: 'getNaverCommerceOrdersFrom' },
                    { label: '토큰 조회', value: 'getNaverCommerceApiToken' },
                    { label: '배송 처리', value: 'deliveryNaveOrder' },
                    { label: '주문 취소', value: 'cancelApproveNaveOrder' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={24} md={6}>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      refetchQueueDetailed();
                      refetchProcessingApi();
                      refetchPendingApi();
                      refetchScheduledApi();
                    }}
                    loading={
                      isLoadingQueueDetailed || isLoadingProcessingApi || isLoadingPendingApi || isLoadingScheduledApi
                    }>
                    새로고침
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 처리 중인 API */}
          <Card title="처리 중인 API" style={{ marginBottom: '24px' }}>
            {isLoadingProcessingApi ? (
              <Spin />
            ) : (
              <Table
                columns={[
                  {
                    title: 'ID',
                    dataIndex: 'id',
                    key: 'id',
                    render: (id: string) => <Text style={{ fontSize: '12px' }}>{id.slice(0, 8)}...</Text>,
                    width: 100,
                  },
                  {
                    title: '메서드',
                    dataIndex: 'method',
                    key: 'method',
                    render: (method: string) => <Tag color="blue">{getMethodDisplayName(method)}</Tag>,
                    width: 120,
                  },
                  {
                    title: '우선순위',
                    dataIndex: 'priority',
                    key: 'priority',
                    render: (priority: string) => (
                      <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
                    ),
                    width: 100,
                  },
                  {
                    title: '재시도 횟수',
                    dataIndex: 'retryCount',
                    key: 'retryCount',
                    render: (retryCount: number, record: any) => (
                      <Text style={{ color: retryCount > 0 ? '#faad14' : '#3f8600' }}>
                        {retryCount}/{record.maxRetries}
                      </Text>
                    ),
                    width: 100,
                  },
                  {
                    title: '생성 시간',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    render: (createdAt: string) => dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss'),
                    width: 150,
                  },
                  {
                    title: '오류 메시지',
                    dataIndex: 'errorMessage',
                    key: 'errorMessage',
                    render: (errorMessage: string) =>
                      errorMessage ? (
                        <Text style={{ fontSize: '12px', color: '#ff4d4f' }} ellipsis={{ tooltip: errorMessage }}>
                          {errorMessage}
                        </Text>
                      ) : (
                        '-'
                      ),
                  },
                ]}
                dataSource={processingApiData?.items || []}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: 800 }}
              />
            )}
          </Card>

          {/* 대기 중인 API */}
          <Card title="대기 중인 API" style={{ marginBottom: '24px' }}>
            {isLoadingPendingApi ? (
              <Spin />
            ) : (
              <Table
                columns={[
                  {
                    title: 'ID',
                    dataIndex: 'id',
                    key: 'id',
                    render: (id: string) => <Text style={{ fontSize: '12px' }}>{id.slice(0, 8)}...</Text>,
                    width: 100,
                  },
                  {
                    title: '메서드',
                    dataIndex: 'method',
                    key: 'method',
                    render: (method: string) => <Tag color="blue">{getMethodDisplayName(method)}</Tag>,
                    width: 120,
                  },
                  {
                    title: '우선순위',
                    dataIndex: 'priority',
                    key: 'priority',
                    render: (priority: string) => (
                      <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
                    ),
                    width: 100,
                  },
                  {
                    title: '상태',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status: string) => (
                      <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
                        {status.toUpperCase()}
                      </Tag>
                    ),
                    width: 100,
                  },
                  {
                    title: '재시도 횟수',
                    dataIndex: 'retryCount',
                    key: 'retryCount',
                    render: (retryCount: number, record: any) => (
                      <Text style={{ color: retryCount > 0 ? '#faad14' : '#3f8600' }}>
                        {retryCount}/{record.maxRetries}
                      </Text>
                    ),
                    width: 100,
                  },
                  {
                    title: '생성 시간',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    render: (createdAt: string) => dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss'),
                    width: 150,
                  },
                  {
                    title: '스케줄 시간',
                    dataIndex: 'scheduledAt',
                    key: 'scheduledAt',
                    render: (scheduledAt: string) =>
                      scheduledAt ? dayjs(scheduledAt).format('YYYY-MM-DD HH:mm:ss') : '-',
                    width: 150,
                  },
                ]}
                dataSource={pendingApiData?.items || []}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: 800 }}
              />
            )}
          </Card>

          {/* 스케줄된 API */}
          <Card title="스케줄된 API (재시도로 인해 지연된 요청)">
            {isLoadingScheduledApi ? (
              <Spin />
            ) : (
              <Table
                columns={[
                  {
                    title: 'ID',
                    dataIndex: 'id',
                    key: 'id',
                    render: (id: string) => <Text style={{ fontSize: '12px' }}>{id.slice(0, 8)}...</Text>,
                    width: 100,
                  },
                  {
                    title: '메서드',
                    dataIndex: 'method',
                    key: 'method',
                    render: (method: string) => <Tag color="blue">{getMethodDisplayName(method)}</Tag>,
                    width: 120,
                  },
                  {
                    title: '우선순위',
                    dataIndex: 'priority',
                    key: 'priority',
                    render: (priority: string) => (
                      <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
                    ),
                    width: 100,
                  },
                  {
                    title: '재시도 횟수',
                    dataIndex: 'retryCount',
                    key: 'retryCount',
                    render: (retryCount: number, record: any) => (
                      <Text style={{ color: retryCount > 0 ? '#faad14' : '#3f8600' }}>
                        {retryCount}/{record.maxRetries}
                      </Text>
                    ),
                    width: 100,
                  },
                  {
                    title: '생성 시간',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    render: (createdAt: string) => dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss'),
                    width: 150,
                  },
                  {
                    title: '스케줄 시간',
                    dataIndex: 'scheduledAt',
                    key: 'scheduledAt',
                    render: (scheduledAt: string) => dayjs(scheduledAt).format('YYYY-MM-DD HH:mm:ss'),
                    width: 150,
                  },
                  {
                    title: '오류 메시지',
                    dataIndex: 'errorMessage',
                    key: 'errorMessage',
                    render: (errorMessage: string) =>
                      errorMessage ? (
                        <Text style={{ fontSize: '12px', color: '#ff4d4f' }} ellipsis={{ tooltip: errorMessage }}>
                          {errorMessage}
                        </Text>
                      ) : (
                        '-'
                      ),
                  },
                ]}
                dataSource={scheduledApiData?.items || []}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: 800 }}
              />
            )}
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default NaverCommerceMonitoringPage;
